import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { thoughtSessionAPI } from "../../lib/api";
import type { ThoughtSessionResponse } from "../../types/api";
import styles from "./SessionSearch.module.css";

interface SessionSearchProps {
  orgId: string;
  /** When provided, the component acts as a controlled filter input and does not render its own results dropdown. */
  onQueryChange?: (query: string) => void;
  /** Override the default placeholder text. */
  placeholder?: string;
  /** Override the default aria-label. */
  ariaLabel?: string;
  /** Override the empty-results noun (e.g. "projects" instead of "thought sessions"). */
  emptyNoun?: string;
}

export function SessionSearch({
  orgId,
  onQueryChange,
  placeholder,
  ariaLabel,
  emptyNoun,
}: SessionSearchProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ThoughtSessionResponse[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setHasSearched(false);
        return;
      }

      setIsSearching(true);
      setError(null);

      try {
        const response = await thoughtSessionAPI.search(orgId, searchQuery, { limit: 10 });
        setResults(response.sessions);
        setHasSearched(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Search failed");
      } finally {
        setIsSearching(false);
      }
    },
    [orgId],
  );

  useEffect(() => {
    // In filter mode, delegate to parent and skip API search
    if (onQueryChange) {
      onQueryChange(query);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch, onQueryChange]);

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setHasSearched(false);
    setError(null);
  };

  const handleResultClick = (session: ThoughtSessionResponse) => {
    navigate(`/organizations/${orgId}/projects/${session.project_id}?session=${session.id}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getPreviewText = (session: ThoughtSessionResponse): string => {
    const userMessages = session.messages.filter((m) => m.role === "user");
    if (userMessages.length === 0) return "";
    return userMessages
      .map((m) => m.content)
      .join(" ")
      .slice(0, 200);
  };

  const statusClass = (status: string) => {
    switch (status) {
      case "active":
        return styles.statusActive;
      case "completed":
        return styles.statusCompleted;
      default:
        return styles.statusArchived;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchHeader}>
        <div className={styles.searchInputWrapper}>
          <span className={styles.searchIcon}>
            <SearchIcon />
          </span>
          <input
            type="text"
            className={styles.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder ?? t("stream.searchThoughts")}
            aria-label={ariaLabel ?? t("stream.searchThoughtSessions")}
          />
          {query && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              aria-label="Clear search"
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {!onQueryChange && (
        <>
          {error && <div className={styles.errorMessage}>{error}</div>}

          {isSearching && (
            <div className={styles.loadingResults}>
              <span className={styles.loadingSpinner} />
              Searching...
            </div>
          )}

          {!isSearching && hasSearched && results.length === 0 && (
            <div className={styles.emptyResults}>
              No {emptyNoun ?? "thought sessions"} found matching &ldquo;{query}&rdquo;
            </div>
          )}

          {results.length > 0 && (
            <div className={styles.results}>
              {results.map((session) => (
                <button
                  key={session.id}
                  type="button"
                  className={styles.resultCard}
                  onClick={() => handleResultClick(session)}
                >
                  <div className={styles.resultMeta}>
                    <span className={`${styles.resultStatus} ${statusClass(session.status)}`}>
                      {session.status}
                    </span>
                    <span className={styles.resultDate}>{formatDate(session.created_at)}</span>
                    <span className={styles.resultMessageCount}>
                      {session.messages.length}{" "}
                      {session.messages.length === 1 ? "message" : "messages"}
                    </span>
                  </div>
                  <p className={styles.resultPreview}>{getPreviewText(session)}</p>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
